import { differenceInCalendarDays, parseISO } from "date-fns";
import { etaIsLate, formatDay, formatEta, formatKES, toNum } from "./format";
import { deliveryLabel } from "./parcel-status";
import type { IParcel } from "./proto";

export type TaskKey = "reply" | "hold" | "settle" | "deliver" | "receipt" | "chase" | "fee" | "late" | "freighter";

export type OpsTask = { key: TaskKey; detail: string };

export type WorkItem = { parcel: IParcel; task: OpsTask };

export const TASK_ORDER: TaskKey[] = ["reply", "hold", "settle", "deliver", "receipt", "chase", "fee", "late", "freighter"];

export const TASK_LABEL: Record<TaskKey, string> = {
  reply: "Reply to customer",
  hold: "Clear the hold",
  settle: "Settle the freighter",
  deliver: "Deliver it",
  receipt: "Confirm warehouse receipt",
  chase: "Chase payment",
  fee: "Set the delivery fee",
  late: "Check on a late parcel",
  freighter: "Add a freighter rate",
};

export const TASK_FILTER_LABEL: Record<TaskKey, string> = {
  reply: "Replies",
  hold: "On hold",
  settle: "To settle",
  deliver: "To deliver",
  receipt: "Warehouse receipt",
  chase: "Unpaid",
  fee: "Delivery fee",
  late: "Late",
  freighter: "No freighter",
};

const CHASE_AFTER_DAYS = 2;

function daysSince(iso?: string | null): number {
  if (!iso) return 0;
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? 0 : differenceInCalendarDays(new Date(), date);
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export function taskFor(parcel: IParcel, unreadMessages: number): OpsTask | null {
  const status = parcel.status ?? "";
  if (unreadMessages > 0) return { key: "reply", detail: `${plural(unreadMessages, "message")} waiting` };
  if (status === "ON_HOLD") return { key: "hold", detail: "Add what is owed or release it" };
  if (status === "PAID") return { key: "settle", detail: `Customer paid ${formatKES(parcel.amountChargedKes)}` };
  if (status === "OUT_FOR_DELIVERY") return { key: "deliver", detail: deliveryLabel(parcel) };
  if (status === "REGISTERED" && parcel.expectedArrival && daysSince(parcel.expectedArrival) >= 0) {
    const asked = parcel.warehouseNotifiedAt ? ", warehouse asked" : "";
    return { key: "receipt", detail: `Seller's date was ${formatDay(parcel.expectedArrival)}${asked}` };
  }
  if (status === "RECEIVED_KENYA" && toNum(parcel.amountChargedKes) > 0 && daysSince(parcel.updatedAt) >= CHASE_AFTER_DAYS) {
    return { key: "chase", detail: `${formatKES(parcel.amountChargedKes)} due for ${plural(daysSince(parcel.updatedAt), "day")}` };
  }
  if (status === "RECEIVED_ABROAD" && toNum(parcel.deliveryFeeKes) === 0) return { key: "fee", detail: deliveryLabel(parcel) };
  if (status === "RECEIVED_ABROAD" && etaIsLate(parcel.etaMaxAt, status)) {
    return { key: "late", detail: `Estimate was ${formatEta(parcel.etaMinAt, parcel.etaMaxAt)}` };
  }
  if (!parcel.freighter) return { key: "freighter", detail: `${parcel.originCountry ?? ""} ${parcel.mode ?? ""}`.trim() };
  return null;
}

export function workItems(parcels: IParcel[], unreadByParcel: Record<string, number>): WorkItem[] {
  const items: WorkItem[] = [];
  for (const parcel of parcels) {
    const task = taskFor(parcel, unreadByParcel[parcel.id ?? ""] ?? 0);
    if (task) items.push({ parcel, task });
  }
  return items.sort(
    (a, b) =>
      TASK_ORDER.indexOf(a.task.key) - TASK_ORDER.indexOf(b.task.key) ||
      (a.parcel.updatedAt ?? "").localeCompare(b.parcel.updatedAt ?? ""),
  );
}
